import React, { useState, useRef, useEffect } from 'react';
import { OptionHistoricData as HistoricData, OptionHistoricResponse } from '../types';
import { apiService } from '../services/apiService';
import { BidAskBarChart } from './BidAskBarChart';
import { DatePicker } from './DatePicker';

interface CalculatedMetrics {
  totalVolume: number;
  nonNeutralVolume: number;
  neutralPercentage: number;
  askPercentage: number;
  bidPercentage: number;
  dominantPercentage: number;
  dominantColor: 'gray' | 'green' | 'red';
}

type SortField = 'date' | 'volume' | 'total_volume' | 'non_neutral_volume' | 'neutral_percentage' | 'ask_percentage' | 'bid_percentage' | 'dominant_percentage' | 'implied_volatility' | 'open_interest' | 'trades' | 'last_price' | 'total_premium' | 'high_price' | 'low_price';
type SortDirection = 'asc' | 'desc';

export const OptionHistoricData: React.FC = () => {
  // Individual input fields for building Option Chain ID
  const [ticker, setTicker] = useState<string>('');
  const [expiryDate, setExpiryDate] = useState<string>('');
  const [optionType, setOptionType] = useState<'C' | 'P'>('C');
  const [strikePrice, setStrikePrice] = useState<string>('');
  
  const [limit, setLimit] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<HistoricData[]>([]);
  const [calculatedMetrics, setCalculatedMetrics] = useState<CalculatedMetrics[]>([]);
  
  // Sorting state
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  
  // Filter state for Total Premium and Search
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [premiumMin, setPremiumMin] = useState<string>('');
  const [premiumMax, setPremiumMax] = useState<string>('');
  const [showPremiumFilter, setShowPremiumFilter] = useState<boolean>(false);
  const [filterDropdownPosition, setFilterDropdownPosition] = useState<{top: number, left: number}>({top: 0, left: 0});
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside or scrolling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showPremiumFilter) {
        const target = event.target as Node;
        const isClickOnButton = filterButtonRef.current && filterButtonRef.current.contains(target);
        const isClickOnDropdown = filterDropdownRef.current && filterDropdownRef.current.contains(target);
        
        if (!isClickOnButton && !isClickOnDropdown) {
          setShowPremiumFilter(false);
        }
      }
    };

    let rafId: number | null = null;
    const updateDropdownPosition = () => {
      if (showPremiumFilter && filterButtonRef.current) {
        const rect = filterButtonRef.current.getBoundingClientRect();
        const dropdownWidth = 300;
        const viewportWidth = window.innerWidth;
        
        // Check if button is still visible (not scrolled out of view too far)
        if (rect.bottom < 0 || rect.top > window.innerHeight) {
          // Button is not visible, close dropdown
          setShowPremiumFilter(false);
          return;
        }
        
        let left = rect.left;
        if (left + dropdownWidth > viewportWidth) {
          left = rect.right - dropdownWidth;
        }
        
        setFilterDropdownPosition({
          top: rect.bottom + 4,
          left: Math.max(10, left)
        });
      }
    };

    const handleScroll = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(updateDropdownPosition);
    };

    if (showPremiumFilter) {
      document.addEventListener('mousedown', handleClickOutside);
      // Listen for scroll events on both window and the table container
      window.addEventListener('scroll', handleScroll, true);
      // Also listen for resize events
      window.addEventListener('resize', handleScroll);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [showPremiumFilter]);

  // Build Option Chain ID from individual components
  const buildOptionChainId = (): string => {
    if (!ticker || !expiryDate || !strikePrice) {
      return '';
    }
    
    // Format strike price to 8 digits (3 decimal + 5 integer)
    const strikeNum = parseFloat(strikePrice);
    if (isNaN(strikeNum)) return '';
    
    // Convert to 8-digit format: multiply by 1000 and pad to 8 digits
    const strikeFormatted = Math.round(strikeNum * 1000).toString().padStart(8, '0');
    
    return `${ticker.toUpperCase()}${expiryDate}${optionType}${strikeFormatted}`;
  };

  // Calculate metrics based on the formulas from the image
  const calculateMetrics = (historicData: HistoricData[]): CalculatedMetrics[] => {
    return historicData.map(item => {
      // Total Volume: volume = bid_volume + ask_volume + mid_volume + neutral_volume
      const totalVolume = item.bid_volume + item.ask_volume + item.mid_volume + item.neutral_volume;
      
      // Non-neutral Volume: non_neutral = bid_volume + ask_volume
      const nonNeutralVolume = item.bid_volume + item.ask_volume;
      
      // Neutral %: (neutral_volume + mid_volume) / volume * 100
      const neutralPercentage = totalVolume > 0 ? ((item.neutral_volume + item.mid_volume) / totalVolume) * 100 : 0;
      
      // Ask %: ask_volume / (bid_volume + ask_volume) * 100
      const askPercentage = nonNeutralVolume > 0 ? (item.ask_volume / nonNeutralVolume) * 100 : 0;
      
      // Bid %: 100 - Ask%
      const bidPercentage = 100 - askPercentage;
      
      // Dominant %: max(Ask%, Bid%, Neutral%)
      const dominantPercentage = Math.max(askPercentage, bidPercentage, neutralPercentage);
      
      // Dominant Color: gray if Neutral%>50 else green if Ask%>Bid% else red
      let dominantColor: 'gray' | 'green' | 'red';
      if (neutralPercentage > 50) {
        dominantColor = 'gray';
      } else if (askPercentage > bidPercentage) {
        dominantColor = 'green';
      } else {
        dominantColor = 'red';
      }

      return {
        totalVolume,
        nonNeutralVolume,
        neutralPercentage,
        askPercentage,
        bidPercentage,
        dominantPercentage,
        dominantColor
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const optionChainId = buildOptionChainId();
    
    if (!optionChainId) {
      setError('Please fill in all required fields (Ticker, Expiry Date, Strike Price)');
      return;
    }

    setLoading(true);
    setError(null);
    setData([]);
    setCalculatedMetrics([]);

    try {
      const limitNum = limit.trim() ? parseInt(limit) : undefined;
      const response: OptionHistoricResponse = await apiService.getOptionHistoricData(optionChainId, limitNum);
      
      setData(response.chains);
      setCalculatedMetrics(calculateMetrics(response.chains));
    } catch (err: any) {
      setError(err.message || 'Failed to fetch historic data');
      console.error('Error fetching historic data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPremium = (premium: string) => {
    const numValue = parseFloat(premium);
    return `$${numValue.toLocaleString()}`;
  };

  const formatPercentage = (percentage: number) => {
    return `${percentage.toFixed(2)}%`;
  };

  const getDominantColorClass = (color: 'gray' | 'green' | 'red') => {
    switch (color) {
      case 'gray': return 'dominant-gray';
      case 'green': return 'dominant-green';
      case 'red': return 'dominant-red';
      default: return '';
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setPremiumMin('');
    setPremiumMax('');
    setShowPremiumFilter(false);
  };

  // Filter and sort data
  const getFilteredAndSortedData = () => {
    let filteredData = data.map((item, index) => ({
      item,
      metrics: calculatedMetrics[index]
    }));

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredData = filteredData.filter(({ item, metrics }) => {
        return (
          item.date.toLowerCase().includes(searchLower) ||
          item.volume.toString().includes(searchLower) ||
          metrics.totalVolume.toString().includes(searchLower) ||
          metrics.nonNeutralVolume.toString().includes(searchLower) ||
          metrics.neutralPercentage.toFixed(2).includes(searchLower) ||
          metrics.askPercentage.toFixed(2).includes(searchLower) ||
          metrics.bidPercentage.toFixed(2).includes(searchLower) ||
          metrics.dominantPercentage.toFixed(2).includes(searchLower) ||
          (item.implied_volatility && item.implied_volatility.toLowerCase().includes(searchLower)) ||
          item.open_interest.toString().includes(searchLower) ||
          item.trades.toString().includes(searchLower) ||
          (item.last_price && item.last_price.toLowerCase().includes(searchLower)) ||
          item.total_premium.toLowerCase().includes(searchLower) ||
          (item.high_price && item.high_price.toLowerCase().includes(searchLower)) ||
          (item.low_price && item.low_price.toLowerCase().includes(searchLower))
        );
      });
    }

    // Apply premium filter
    if (premiumMin.trim() || premiumMax.trim()) {
      filteredData = filteredData.filter(({ item }) => {
        const totalPremium = parseFloat(item.total_premium);
        if (isNaN(totalPremium)) return false;
        
        const minValue = premiumMin.trim() ? parseFloat(premiumMin) : 0;
        const maxValue = premiumMax.trim() ? parseFloat(premiumMax) : Infinity;
        
        return totalPremium >= minValue && totalPremium <= maxValue;
      });
    }

    // Sort data
    filteredData.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      // Get values based on sort field
      switch (sortField) {
        case 'total_volume':
          aValue = a.metrics.totalVolume;
          bValue = b.metrics.totalVolume;
          break;
        case 'non_neutral_volume':
          aValue = a.metrics.nonNeutralVolume;
          bValue = b.metrics.nonNeutralVolume;
          break;
        case 'neutral_percentage':
          aValue = a.metrics.neutralPercentage;
          bValue = b.metrics.neutralPercentage;
          break;
        case 'ask_percentage':
          aValue = a.metrics.askPercentage;
          bValue = b.metrics.askPercentage;
          break;
        case 'bid_percentage':
          aValue = a.metrics.bidPercentage;
          bValue = b.metrics.bidPercentage;
          break;
        case 'dominant_percentage':
          aValue = a.metrics.dominantPercentage;
          bValue = b.metrics.dominantPercentage;
          break;
        case 'date':
          aValue = new Date(a.item.date).getTime();
          bValue = new Date(b.item.date).getTime();
          break;
        case 'volume':
          aValue = a.item.volume;
          bValue = b.item.volume;
          break;
        case 'implied_volatility':
          aValue = parseFloat(a.item.implied_volatility) || 0;
          bValue = parseFloat(b.item.implied_volatility) || 0;
          break;
        case 'open_interest':
          aValue = a.item.open_interest;
          bValue = b.item.open_interest;
          break;
        case 'trades':
          aValue = a.item.trades;
          bValue = b.item.trades;
          break;
        case 'last_price':
          aValue = parseFloat(a.item.last_price || '0');
          bValue = parseFloat(b.item.last_price || '0');
          break;
        case 'total_premium':
          aValue = parseFloat(a.item.total_premium);
          bValue = parseFloat(b.item.total_premium);
          break;
        case 'high_price':
          aValue = parseFloat(a.item.high_price || '0');
          bValue = parseFloat(b.item.high_price || '0');
          break;
        case 'low_price':
          aValue = parseFloat(a.item.low_price || '0');
          bValue = parseFloat(b.item.low_price || '0');
          break;
        default:
          aValue = 0;
          bValue = 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filteredData;
  };

  const filteredAndSortedData = getFilteredAndSortedData();

  return (
    <div className="component-container">
      <h2 className="component-title">Option Historic Data</h2>
      
      {/* Input Form */}
      <div className="historic-form-container">
        <form onSubmit={handleSubmit} className="historic-form">
          {/* First Row: Ticker and Expiry Date */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="ticker" className="form-label">
                Ticker Symbol
              </label>
              <input
                type="text"
                id="ticker"
                value={ticker}
                onChange={(e) => setTicker(e.target.value.toUpperCase())}
                placeholder="e.g., SPOT"
                className="form-input"
                disabled={loading}
                maxLength={6}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="expiryDate" className="form-label">
                Expiry Date
              </label>
              <DatePicker
                value={expiryDate}
                onChange={setExpiryDate}
                placeholder="YY/MM/DD"
                disabled={loading}
                className="date-picker"
              />
            </div>
          </div>
          
          {/* Second Row: Option Type and Strike Price */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="optionType" className="form-label">
                Option Type
              </label>
              <select
                id="optionType"
                value={optionType}
                onChange={(e) => setOptionType(e.target.value as 'C' | 'P')}
                className="form-input"
                disabled={loading}
              >
                <option value="C">Call (C)</option>
                <option value="P">Put (P)</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="strikePrice" className="form-label">
                Strike Price
              </label>
              <input
                type="number"
                id="strikePrice"
                value={strikePrice}
                onChange={(e) => setStrikePrice(e.target.value)}
                placeholder="e.g., 710.00"
                className="form-input"
                disabled={loading}
                step="0.01"
                min="0"
              />
            </div>
          </div>
          
          {/* Third Row: Limit and Generated ID */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="limit" className="form-label">
                Limit (Optional)
              </label>
              <input
                type="number"
                id="limit"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                placeholder="Max records"
                className="form-input"
                disabled={loading}
                min="1"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">
                Generated Option Chain ID
              </label>
              <div className="option-chain-display">
                {buildOptionChainId() || 'Fill in required fields'}
              </div>
            </div>
          </div>
          
          <button 
            type="submit" 
            className="form-submit"
            disabled={loading || !buildOptionChainId()}
          >
            {loading ? 'Loading...' : 'Fetch Historic Data'}
          </button>
        </form>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-container">
          <p className="error-text">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span className="loading-text">Loading historic data...</span>
        </div>
      )}

      {/* Search Bar */}
      {data.length > 0 && (
        <div className="search-container" style={{ marginBottom: '20px' }}>
          <div className="search-wrapper">
            <div className="search-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search any data..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
              className="search-input"
              style={{ textTransform: 'uppercase' }}
            />
            {(searchTerm || premiumMin || premiumMax) && (
              <button
                onClick={handleClearFilters}
                className="search-clear"
                title="Clear all filters"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      )}

      {/* Data Table */}
      {data.length > 0 && (
        <div className="table-container">
          <div className="table-header-info">
            <h3>Historic Data for {buildOptionChainId()}</h3>
            <p>Showing {filteredAndSortedData.length} of {data.length} records with calculated metrics</p>
          </div>
          
          <table className="table">
            <thead className="table-header">
              <tr>
                <th 
                  className="table-header-cell"
                  onClick={() => handleSort('date')}
                >
                  <div className="table-header-content">
                    Date {getSortIcon('date')}
                  </div>
                </th>
                <th 
                  className="table-header-cell"
                  onClick={() => handleSort('total_premium')}
                >
                  <div className="table-header-content">
                    Total Premium {getSortIcon('total_premium')}
                    <button
                      ref={filterButtonRef}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (filterButtonRef.current) {
                          const rect = filterButtonRef.current.getBoundingClientRect();
                          const dropdownWidth = 300;
                          const viewportWidth = window.innerWidth;
                          
                          let left = rect.left;
                          if (left + dropdownWidth > viewportWidth) {
                            left = rect.right - dropdownWidth;
                          }
                          
                          setFilterDropdownPosition({
                            top: rect.bottom + 4,
                            left: Math.max(10, left)
                          });
                        }
                        setShowPremiumFilter(!showPremiumFilter);
                      }}
                      className="column-filter-button"
                      title="Filter by premium range"
                    >
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                    </button>
                  </div>
                </th>
                <th 
                  className="table-header-cell"
                  onClick={() => handleSort('volume')}
                >
                  <div className="table-header-content">
                    Volume {getSortIcon('volume')}
                  </div>
                </th>
                <th 
                  className="table-header-cell"
                  onClick={() => handleSort('total_volume')}
                >
                  <div className="table-header-content">
                    Total Volume {getSortIcon('total_volume')}
                  </div>
                </th>
                <th 
                  className="table-header-cell"
                  onClick={() => handleSort('non_neutral_volume')}
                >
                  <div className="table-header-content">
                    Non-neutral Volume {getSortIcon('non_neutral_volume')}
                  </div>
                </th>
                <th 
                  className="table-header-cell"
                  onClick={() => handleSort('neutral_percentage')}
                >
                  <div className="table-header-content">
                    Neutral % {getSortIcon('neutral_percentage')}
                  </div>
                </th>
                <th 
                  className="table-header-cell"
                  onClick={() => handleSort('ask_percentage')}
                >
                  <div className="table-header-content">
                    Ask % {getSortIcon('ask_percentage')}
                  </div>
                </th>
                <th 
                  className="table-header-cell"
                  onClick={() => handleSort('bid_percentage')}
                >
                  <div className="table-header-content">
                    Bid % {getSortIcon('bid_percentage')}
                  </div>
                </th>
                <th 
                  className="table-header-cell"
                  onClick={() => handleSort('dominant_percentage')}
                >
                  <div className="table-header-content">
                    Dominant % {getSortIcon('dominant_percentage')}
                  </div>
                </th>
                <th className="table-header-cell">Dominant Color</th>
                <th className="table-header-cell">Bid/Ask Chart</th>
                <th 
                  className="table-header-cell"
                  onClick={() => handleSort('implied_volatility')}
                >
                  <div className="table-header-content">
                    Implied Volatility {getSortIcon('implied_volatility')}
                  </div>
                </th>
                <th 
                  className="table-header-cell"
                  onClick={() => handleSort('open_interest')}
                >
                  <div className="table-header-content">
                    Open Interest {getSortIcon('open_interest')}
                  </div>
                </th>
                <th 
                  className="table-header-cell"
                  onClick={() => handleSort('trades')}
                >
                  <div className="table-header-content">
                    Trades {getSortIcon('trades')}
                  </div>
                </th>
                <th 
                  className="table-header-cell"
                  onClick={() => handleSort('last_price')}
                >
                  <div className="table-header-content">
                    Last Price {getSortIcon('last_price')}
                  </div>
                </th>
                <th 
                  className="table-header-cell"
                  onClick={() => handleSort('high_price')}
                >
                  <div className="table-header-content">
                    High Price {getSortIcon('high_price')}
                  </div>
                </th>
                <th 
                  className="table-header-cell"
                  onClick={() => handleSort('low_price')}
                >
                  <div className="table-header-content">
                    Low Price {getSortIcon('low_price')}
                  </div>
                </th>
              </tr>
            </thead>
            
            {/* Premium Filter Dropdown */}
            {showPremiumFilter && (
              <div 
                ref={filterDropdownRef}
                className="column-filter-dropdown"
                style={{
                  top: `${filterDropdownPosition.top}px`,
                  left: `${filterDropdownPosition.left}px`
                }}
              >
                <div className="column-filter-content">
                  <div className="column-filter-header">
                    <h4>Filter by Total Premium</h4>
                    <button
                      onClick={() => setShowPremiumFilter(false)}
                      className="column-filter-close"
                    >
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="column-filter-inputs">
                    <div className="column-filter-input-group">
                      <label>Minimum Premium</label>
                      <input
                        type="number"
                        placeholder="Min ($)"
                        value={premiumMin}
                        onChange={(e) => setPremiumMin(e.target.value)}
                        className="column-filter-input"
                      />
                    </div>
                    <div className="column-filter-input-group">
                      <label>Maximum Premium</label>
                      <input
                        type="number"
                        placeholder="Max ($)"
                        value={premiumMax}
                        onChange={(e) => setPremiumMax(e.target.value)}
                        className="column-filter-input"
                      />
                    </div>
                  </div>
                  <div className="column-filter-actions">
                    <button
                      onClick={() => {
                        setPremiumMin('');
                        setPremiumMax('');
                      }}
                      className="column-filter-clear"
                    >
                      Clear
                    </button>
                    <button
                      onClick={() => setShowPremiumFilter(false)}
                      className="column-filter-apply"
                    >
                      Apply Filter
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <tbody className="table-body">
              {filteredAndSortedData.map(({ item, metrics }, index) => (
                  <tr key={index} className="table-row">
                    <td className="table-cell table-cell-primary">{item.date}</td>
                  <td className="table-cell table-cell-primary">{formatPremium(item.total_premium)}</td>
                    <td className="table-cell table-cell-secondary">{item.volume.toLocaleString()}</td>
                    <td className="table-cell table-cell-secondary">{metrics.totalVolume.toLocaleString()}</td>
                    <td className="table-cell table-cell-secondary">{metrics.nonNeutralVolume.toLocaleString()}</td>
                    <td className="table-cell table-cell-secondary">{formatPercentage(metrics.neutralPercentage)}</td>
                    <td className="table-cell table-cell-secondary">{formatPercentage(metrics.askPercentage)}</td>
                    <td className="table-cell table-cell-secondary">{formatPercentage(metrics.bidPercentage)}</td>
                    <td className="table-cell table-cell-secondary">{formatPercentage(metrics.dominantPercentage)}</td>
                    <td className="table-cell table-cell-secondary">
                      <span className={`dominant-color-indicator ${getDominantColorClass(metrics.dominantColor)}`}>
                        {metrics.dominantColor.toUpperCase()}
                      </span>
                    </td>
                    <td className="table-cell table-cell-secondary">
                      <BidAskBarChart
                        askPercentage={metrics.askPercentage}
                        bidPercentage={metrics.bidPercentage}
                        neutralPercentage={metrics.neutralPercentage}
                        className="table-bid-ask-chart"
                      />
                    </td>
                    <td className="table-cell table-cell-secondary">
                      {item.implied_volatility ? (parseFloat(item.implied_volatility) * 100).toFixed(2) + '%' : 'N/A'}
                    </td>
                    <td className="table-cell table-cell-secondary">{item.open_interest.toLocaleString()}</td>
                    <td className="table-cell table-cell-secondary">{item.trades}</td>
                    <td className="table-cell table-cell-secondary">
                      {item.last_price ? `$${parseFloat(item.last_price).toFixed(2)}` : 'N/A'}
                    </td>
                    <td className="table-cell table-cell-secondary">
                      {item.high_price ? `$${parseFloat(item.high_price).toFixed(2)}` : 'N/A'}
                    </td>
                    <td className="table-cell table-cell-secondary">
                      {item.low_price ? `$${parseFloat(item.low_price).toFixed(2)}` : 'N/A'}
                    </td>
                  </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {!loading && data.length === 0 && !error && (
        <div className="empty-state">
          <p>Enter an option chain ID to fetch historic data</p>
          <p className="empty-state-help">
            Example: SPOT251031C00710000 (SPOT $710 Call expiring Oct 31, 2025)
          </p>
        </div>
      )}
    </div>
  );
};
